import { inject, InjectionToken } from '@angular/core';

import { createSelector, StoreConfig } from '@ngrx/store';

import { LOCALE } from '@recruitee/i18n';
import { createSingleEntityStore, memoizeSelector, SingleEntityState } from '@recruitee/ngrx';

import jsonData from '../assets/countries.json';
import {
  CountryMap,
  EntityStoreKey,
  CountryStoreEntity,
  ParsedJsonCountry,
  Country,
} from '../types';

type CountryStoreConfig = StoreConfig<SingleEntityState<CountryStoreEntity>>;

const { reducer, namespace, selectors } =
  createSingleEntityStore<CountryStoreEntity>()(EntityStoreKey);

export const COUNTRY_STORE_CONFIG = new InjectionToken<CountryStoreConfig>('COUNTRY_STORE_CONFIG');

export function countryStoreConfigFactory(): CountryStoreConfig {
  return {
    initialState: {
      loaded: true,
      pending: false,
      failed: false,
      error: null,
      data: {
        countries: jsonData,
        currentLocale: inject(LOCALE, { optional: true }) ?? undefined,
      },
    },
  };
}

const mapLocalizedName = (langCode: string) => (country: ParsedJsonCountry) => {
  if (!country['names'][langCode]) {
    throw new Error(`Unsupported locale: ${langCode}`);
  }
  return <Country>{
    ...country,
    name: country.names[langCode],
  };
};
const reduceToMap = (acc: CountryMap, curr: Country) => ({ ...acc, [curr.code]: curr });

const byLanguage = memoizeSelector((code: string) =>
  createSelector(selectors.data, data => data.countries.map(mapLocalizedName(code))),
);

const byLanguageAsMap = memoizeSelector((code: string) =>
  createSelector(byLanguage(code), collection => <CountryMap>collection.reduce(reduceToMap, {})),
);

const asList = createSelector(selectors.data, state => {
  if (!state.currentLocale) {
    console.warn('LOCALE not injected, state.currentLocale not set');
  }
  return state.currentLocale ? state.countries.map(mapLocalizedName(state.currentLocale)) : [];
});

const asMap = createSelector(asList, data => <CountryMap>data.reduce(reduceToMap, {}));

const CountrySelectors = {
  byLanguage,
  byLanguageAsMap,
  asList,
  asMap,
};

export { CountrySelectors, reducer as CountryDataReducer, namespace as CountryDataNamespace };
